import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { getContext } from 'configs/context';

import { extractPathnameFromSearch } from 'utils/routes';
import { createRoutes } from 'utils/constants';

interface RoutesContextValue {
  pathname: string;
  params: any;
  routes: any;
}

const getBasePath = (): string => {
  return '/batch';
};

const RoutesContext = createContext<RoutesContextValue | null>(null);

interface RoutesProviderProps {
  children: ReactNode;
}

function RoutesProvider({ children }: RoutesProviderProps) {
  const locationContext = getContext().general.location;
  const [pathname, setPathname] = useState('');
  const [params, setParams] = useState<Record<string, string | string[]>>({});
  const [routes, setRoutes] = useState<any>(null);

  useEffect(
    () =>
      locationContext.subscribe(
        nextValue => {
          const extractedParams = nextValue.params;
          setParams(extractedParams);

          let { route } = extractPathnameFromSearch(nextValue.params);
          const basePath = getBasePath();
          const source = createRoutes(basePath);
          setRoutes(source);
          if (!route || (route && route.endsWith('/'))) {
            route = basePath;
          }

          setPathname(route);
        },
        { needPrevious: true },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Memoize context value to avoid unnecessary renders
  const contextValue = useMemo<RoutesContextValue>(
    () => ({
      pathname,
      params,
      routes,
    }),
    [pathname, params, routes],
  );

  // Render null if pathname is not available yet
  if (!pathname) {
    return null;
  }

  // Render provider with context value and children
  return <RoutesContext.Provider value={contextValue}>{children}</RoutesContext.Provider>;
}

// Custom hook to consume the routes context
export const useRouter = (): RoutesContextValue | null => useContext(RoutesContext);

export default RoutesProvider;
